import { useMemo } from 'react';
import {
  useTreatmentCourseTemplate,
  useTreatmentCourseTemplateByCatalogService,
} from '@/hooks/useTreatmentCourseTemplates';
import {
  allowedQuestionnaireIdsForAppointmentType,
  bookedStepQuestionnaireIds,
  generalAllowedQuestionnaireIds,
  normalizeQuestionnaireIds,
  resolveJournalQuestionnaireAllowList,
  snapshotStepAllowList,
  templateStepAllowedIds,
  type JournalQuestionnaireAllowSource,
} from '@/lib/journalAppointmentQuestionnaires';

type AllowCourse = {
  template_id?: string | null;
  template_snapshot?: unknown;
  general_allowed_forms_template_ids?: string[] | null;
} | null;

type AllowStep = {
  allowed_forms_template_ids?: readonly string[] | null;
  template_step_id?: string | null;
  sort_order?: number | null;
  service_id?: string | null;
  questionnaire_mode?: string | null;
} | null;

/**
 * Tilladte for one booked forløb step.
 *
 * Same chain as the journal tools on that step: the booked column, then the
 * booking snapshot, then the live template step at this trin's position.
 * Generelt is used when the step itself is frit valg and that list exists.
 * Inherit steps with no list of their own then use the aftaletype Tilladte.
 */
export function useCourseStepQuestionnaireAllowList(args: {
  clinicId: string | null | undefined;
  course: AllowCourse;
  step: AllowStep;
}): {
  ids: string[];
  source: JournalQuestionnaireAllowSource;
  isLoading: boolean;
  isError: boolean;
} {
  const { clinicId, course, step } = args;
  const columnIds = useMemo(
    () => normalizeQuestionnaireIds(step?.allowed_forms_template_ids),
    [step],
  );
  const snapshotAllow = useMemo(
    () => snapshotStepAllowList(course?.template_snapshot, step),
    [course?.template_snapshot, step],
  );
  // Booking stores `[]` when the join table was empty at insert. A later
  // template save can also mint new step ids, so the snapshot stays empty
  // while the step card has Tilladte. Read the live template only then.
  const needLiveTemplate = Boolean(course && step) && columnIds.length === 0 && snapshotAllow.ids.length === 0;
  const liveTemplate = useTreatmentCourseTemplate(
    clinicId,
    needLiveTemplate ? (course?.template_id ?? null) : null,
  );
  const stepOwnIds = useMemo(
    () =>
      bookedStepQuestionnaireIds({
        columnIds,
        snapshot: snapshotAllow,
        liveTemplateIds: needLiveTemplate ? templateStepAllowedIds(liveTemplate.data, step) : [],
      }),
    [columnIds, liveTemplate.data, needLiveTemplate, snapshotAllow, step],
  );
  const generalAllowedIds = useMemo(
    () =>
      course
        ? generalAllowedQuestionnaireIds({
            course,
            template: liveTemplate.data,
          })
        : null,
    [course, liveTemplate.data],
  );
  const fromCourse = useMemo(
    () =>
      resolveJournalQuestionnaireAllowList({
        stepAllowedIds: stepOwnIds,
        generalAllowedIds,
      }),
    [generalAllowedIds, stepOwnIds],
  );
  // Replace keeps only the course step's own list. Inherit still uses the
  // aftaletype Tilladte when this trin has none of its own, so trin 2–5 are
  // not stuck on frit valg just because the forløb step card is empty.
  const inheritService = step?.questionnaire_mode !== 'replace';
  const serviceId = inheritService ? (step?.service_id ?? null) : null;
  const mayNeedService = Boolean(serviceId) && fromCourse.ids.length === 0;
  const serviceTemplate = useTreatmentCourseTemplateByCatalogService(
    clinicId,
    mayNeedService ? serviceId : null,
  );
  const serviceIds = useMemo(
    () => (mayNeedService ? allowedQuestionnaireIdsForAppointmentType(serviceTemplate.data) : []),
    [mayNeedService, serviceTemplate.data],
  );
  const resolved =
    fromCourse.ids.length > 0
      ? fromCourse
      : serviceIds.length > 0
        ? { source: 'step' as const, ids: serviceIds }
        : fromCourse;

  return {
    ids: resolved.ids,
    source: resolved.source,
    isLoading:
      (needLiveTemplate && liveTemplate.isLoading) ||
      (mayNeedService && serviceTemplate.isLoading),
    isError:
      (needLiveTemplate && liveTemplate.isError && serviceIds.length === 0) ||
      (mayNeedService && serviceTemplate.isError && fromCourse.ids.length === 0),
  };
}
